/**
 * "These paths have no formula" — names them, and says what it means.
 * For Media Devoted | Created By Ciro Autiero
 *
 * A warning, never a save gate: skipping paths is a legitimate configuration.
 * What it must not do is let a path fall out of the maths silently, which is
 * exactly what happens without it — the path keeps its weight and nothing on
 * screen says why.
 */

import { FC } from 'react';
import { AlertTriangle } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { UncoveredPath, UncoveredReason } from './uncovered-paths';

const HEADLINE: Record<UncoveredReason, (n: number) => string> = {
  'ignored-by-all': (n) =>
    `${n} path${n === 1 ? ' is' : 's are'} skipped by every formula`,
  'no-formula-layer': (n) =>
    `${n} path${n === 1 ? '' : 's'} sit${n === 1 ? 's' : ''} on a layer no formula targets`,
};

const DETAIL: Record<UncoveredReason, string> = {
  'ignored-by-all':
    'They keep the weight they have now and are left out of the calculation, so the other paths share the full 100%.',
  'no-formula-layer':
    'Add a formula that applies to paths, or they will all keep the weights they have now.',
};

const PathChip: FC<{ path: UncoveredPath }> = ({ path }) => (
  <span
    className="rounded border border-warning/40 bg-warning/10 px-1.5 py-px text-[10.5px] font-medium"
    title={path.itemId}
  >
    {path.itemName}
  </span>
);

const Section: FC<{ reason: UncoveredReason; paths: UncoveredPath[] }> = ({
  reason,
  paths,
}) => (
  <div className="space-y-1">
    <p className="font-semibold">{HEADLINE[reason](paths.length)}</p>
    <p className="text-[10.5px] opacity-90">{DETAIL[reason]}</p>
    <div className="flex flex-wrap gap-1">
      {paths.slice(0, 12).map((p) => (
        <PathChip key={p.itemId} path={p} />
      ))}
      {paths.length > 12 ? (
        <span
          className="text-[10.5px] opacity-80"
          title={paths.map((p) => p.itemName).join('\n')}
        >
          +{paths.length - 12} more
        </span>
      ) : null}
    </div>
  </div>
);

export const UncoveredPathsWarning: FC<{
  paths: UncoveredPath[];
  className?: string;
}> = ({ paths, className }) => {
  const open = paths.length > 0;
  const byReason: UncoveredReason[] = ['ignored-by-all', 'no-formula-layer'];
  return (
    <div
      aria-hidden={!open}
      className={cn(
        'grid transition-[grid-template-rows,opacity] duration-200 ease-out',
        open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        className,
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-2.5 text-[11px] text-warning"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="min-w-0 space-y-2">
            {byReason.map((reason) => {
              const group = paths.filter((p) => p.reason === reason);
              return group.length ? (
                <Section key={reason} reason={reason} paths={group} />
              ) : null;
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UncoveredPathsWarning;
